import axios from 'axios';
import secureLocalStorage from 'react-secure-storage';
import store from '../app/store';
import { save } from '../features/token/tokenSaver';
import {
    ensureValidAccessToken,
    performTokenRefresh,
    redirectToLogin,
    resolveActiveRole,
} from './ensureSessionCredentials';
import { getUserAccessToken } from './userSession';

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Public auth endpoints must never trigger token attachment or refresh:
// - /refreshJWT failing with 401 would otherwise trigger another refresh that
//   awaits its own pending promise, deadlocking every queued request forever.
// - Login endpoints failing with 401 (wrong credentials) must surface the
//   error to the form instead of starting a refresh/redirect cycle.
// - /checkJWT callers set the Authorization header explicitly to validate a
//   specific token, which the interceptor must not overwrite.
const AUTH_ENDPOINTS = [
  '/refreshJWT',
  '/checkJWT',
  '/adminLogin',
  '/advertiserLogin',
  '/userLogin',
  '/screenHostLogin',
];

const isAuthEndpoint = (config) => {
  const url = String(config?.url || '');
  return AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));
};

export const resetAuthInterceptorState = () => {
  isRefreshing = false;
  failedQueue = [];
};

const stripAuthorizationHeader = (config) => {
  if (config?.headers) {
    delete config.headers.Authorization;
    delete config.headers.authorization;
  }
  return config;
};

const attachAuthorizationHeader = (config, token) => {
  if (token) {
    config.headers.Authorization = token;
  }
  return config;
};

const readStoredAccessToken = () =>
  store.getState().token.value || getUserAccessToken() || secureLocalStorage.getItem('tk');

// Proactively refresh expired tokens before requests go out.
axios.interceptors.request.use(
  async (config) => {
    if (isAuthEndpoint(config)) {
      return stripAuthorizationHeader(config);
    }

    const role = resolveActiveRole();
    if (!role) {
      return config;
    }

    const token = await ensureValidAccessToken(role);
    return attachAuthorizationHeader(config, token || readStoredAccessToken());
  },
  (error) => Promise.reject(error)
);

// Fallback refresh when a request still fails with an expired token.
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isUnauthorized = error.response?.status === 401;
    const isExpiredToken = error.response?.status === 403 && error.response?.data?.error === 'EXPTOKEN';

    // Auth endpoints handle their own failures (login forms show the error,
    // refresh failures are handled by whoever initiated the refresh).
    // Attempting a refresh here would recurse into a deadlock.
    if (isAuthEndpoint(originalRequest)) {
      resetAuthInterceptorState();
      return Promise.reject(error);
    }

    if ((isUnauthorized || isExpiredToken) && originalRequest && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = token;
            return axios(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const role = resolveActiveRole();

      if (!role) {
        processQueue(error, null);
        isRefreshing = false;
        redirectToLogin(null);
        return Promise.reject(error);
      }

      try {
        const newToken = await performTokenRefresh(role);
        originalRequest.headers.Authorization = newToken;
        processQueue(null, newToken);
        isRefreshing = false;
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;
        redirectToLogin(role);
        return Promise.reject(refreshError);
      }
    }

    // The token was already refreshed and retried but the request still comes
    // back unauthorized. Don't give up on a broken session — send the user to
    // the appropriate login screen instead of leaving them on a dead page.
    if (isUnauthorized && originalRequest?._retry) {
      redirectToLogin(resolveActiveRole());
    }

    return Promise.reject(error);
  }
);

export default axios;
